import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import axios from '../../utils/axios';
import useLocalStorageState from '../../utils/hooks/useLocalStorageState';
import { API_URL, stateArray } from '../../constants';
import { SUBMISSION_DETAIL_ROUTE } from './SubmissionDetailView';
import PhotoCapture from './PhotoCapture';
import {
  DRAFT_KEY,
  Draft,
  EMAIL_PATTERN,
  EMPTY_DRAFT,
  MIN_YEAR,
  PhotoField,
  ZIP_PATTERN,
  canCreateSubmissionOnBehalf,
  hasDraftContent,
  isValidYear,
  missingFields,
  submitDraft,
  uploadPhoto,
} from './createSubmissionOnBehalfShared';

export const CREATE_SUBMISSION_ON_BEHALF_ROUTE = '/buyers/tradeups/create-submission';
export const MOBILE_CREATE_SUBMISSION_ROUTE = '/mobile/create-submission';

const FIELD_ID = 'create-submission';

const Section: React.FC<{ title: string }> = ({ title, children }) => (
  <Box display="flex" flexDirection="column" gap={2}>
    <Typography variant="h6">{title}</Typography>
    {children}
  </Box>
);

/**
 * Staff form for creating a bike submission on a customer's behalf. Built for a phone or tablet:
 * one column, large touch targets, mobile keyboards, camera or library photos, and a submit
 * button pinned to the bottom of the screen.
 */
export const CreateSubmissionOnBehalf: React.FC = () => {
  const history = useHistory();
  const [draft, setDraft] = useLocalStorageState<Draft>(DRAFT_KEY, EMPTY_DRAFT);
  const [brands, setBrands] = useState<string[]>([]);
  const [progress, setProgress] = useState<Record<PhotoField, number | null>>({
    imageDriveSideUrl: null,
    imageNonDriveSideUrl: null,
  });
  const [photoErrors, setPhotoErrors] = useState<Record<PhotoField, string | undefined>>({
    imageDriveSideUrl: undefined,
    imageNonDriveSideUrl: undefined,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [createdId, setCreatedId] = useState<number | undefined>();
  const [confirmingReset, setConfirmingReset] = useState(false);

  const allowed = canCreateSubmissionOnBehalf();

  useEffect(() => {
    if (!allowed) {
      return;
    }
    axios
      .get(`${API_URL}/tradeup/syb/brands`)
      .then(res => setBrands(res ? res.data.map((brand: { name: string }) => brand.name) : []))
      .catch(() => setBrands([]));
  }, [allowed]);

  if (!allowed) {
    return (
      <Box p={3}>
        <Typography>
          You do not have permission to create a submission on behalf of a customer.
        </Typography>
      </Box>
    );
  }

  const update = (field: keyof Draft, value: string) =>
    setDraft(current => ({ ...current, [field]: value }));

  const handlePhoto = async (field: PhotoField, file: File) => {
    setPhotoErrors(current => ({ ...current, [field]: undefined }));
    setProgress(current => ({ ...current, [field]: 0 }));
    try {
      const url = await uploadPhoto(file, percent =>
        setProgress(current => ({ ...current, [field]: percent })),
      );
      update(field, url);
    } catch (err) {
      console.error('Photo upload failed', err);
      setPhotoErrors(current => ({ ...current, [field]: err.message || 'Photo upload failed' }));
    } finally {
      setProgress(current => ({ ...current, [field]: null }));
    }
  };

  const clearPhoto = (field: PhotoField) => {
    update(field, '');
    setPhotoErrors(current => ({ ...current, [field]: undefined }));
  };

  const resetForm = () => {
    setDraft(EMPTY_DRAFT);
    setPhotoErrors({ imageDriveSideUrl: undefined, imageNonDriveSideUrl: undefined });
    setError(undefined);
    setConfirmingReset(false);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(undefined);
    try {
      const submissionId = await submitDraft(draft);
      if (submissionId === undefined) {
        // The axios wrapper is reloading the page for an expired session
        return;
      }
      setDraft(EMPTY_DRAFT);
      setCreatedId(submissionId);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.response?.data?.message || 'An unknown error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  if (createdId !== undefined) {
    return (
      <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 2 }}>
        <Alert severity="success">
          Submission #{createdId} created. The customer will get an offer email once a buyer
          proposes one.
        </Alert>
        <Button
          size="large"
          variant="contained"
          sx={{ minHeight: 52 }}
          onClick={() => setCreatedId(undefined)}
        >
          Create another submission
        </Button>
        <Button
          size="large"
          variant="outlined"
          sx={{ minHeight: 52 }}
          onClick={() => history.push(`${SUBMISSION_DETAIL_ROUTE}/${createdId}`)}
        >
          View submission #{createdId}
        </Button>
      </Box>
    );
  }

  const emailInvalid = !!draft.email && !EMAIL_PATTERN.test(draft.email);
  const zipInvalid = !!draft.zip && !ZIP_PATTERN.test(draft.zip);
  const yearInvalid = !!draft.year && draft.year.length === 4 && !isValidYear(draft.year);
  const missing = missingFields(draft);
  const uploading = Object.values(progress).some(value => value !== null);
  const canSubmit = missing.length === 0 && !uploading && !submitting;

  return (
    <Box display="flex" flexDirection="column" gap={3} sx={{ pb: 2, maxWidth: 560 }}>
      <Box>
        <Typography variant="h5">Create Submission</Typography>
        <Typography variant="body2" color="text.secondary">
          Creates a bike submission on the customer&apos;s behalf. If they have no account yet, one
          is created unactivated; they activate it from the offer email.
        </Typography>
        {hasDraftContent(draft) && (
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Progress is saved on this device.
            </Typography>
            <Button
              size="small"
              color="error"
              disabled={uploading || submitting}
              onClick={() => setConfirmingReset(true)}
              sx={{ minHeight: 40 }}
            >
              Start over
            </Button>
          </Box>
        )}
      </Box>

      <Section title="Seller">
        <Box display="flex" gap={2}>
          <TextField
            id={`${FIELD_ID}-firstName`}
            label="First name"
            value={draft.firstName}
            onChange={e => update('firstName', e.target.value)}
            autoComplete="off"
            inputProps={{ autoCapitalize: 'words' }}
            fullWidth
          />
          <TextField
            id={`${FIELD_ID}-lastName`}
            label="Last name"
            value={draft.lastName}
            onChange={e => update('lastName', e.target.value)}
            autoComplete="off"
            inputProps={{ autoCapitalize: 'words' }}
            fullWidth
          />
        </Box>
        <TextField
          id={`${FIELD_ID}-email`}
          label="Email"
          type="email"
          value={draft.email}
          error={emailInvalid}
          helperText={emailInvalid ? 'Enter a valid email' : undefined}
          onChange={e => update('email', e.target.value)}
          autoComplete="off"
          inputProps={{ inputMode: 'email', autoCapitalize: 'none', autoCorrect: 'off' }}
          fullWidth
        />
        <TextField
          id={`${FIELD_ID}-phone`}
          label="Phone"
          type="tel"
          value={draft.phone}
          onChange={e => update('phone', e.target.value)}
          autoComplete="off"
          inputProps={{ inputMode: 'tel' }}
          fullWidth
        />
      </Section>

      <Section title="Mailing address">
        <TextField
          id={`${FIELD_ID}-address`}
          label="Street address"
          value={draft.address}
          onChange={e => update('address', e.target.value)}
          autoComplete="off"
          inputProps={{ autoCapitalize: 'words' }}
          fullWidth
        />
        <TextField
          id={`${FIELD_ID}-city`}
          label="City"
          value={draft.city}
          onChange={e => update('city', e.target.value)}
          autoComplete="off"
          inputProps={{ autoCapitalize: 'words' }}
          fullWidth
        />
        <Box display="flex" gap={2}>
          <TextField
            id={`${FIELD_ID}-state`}
            label="State"
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            value={draft.state}
            onChange={e => update('state', e.target.value)}
            fullWidth
          >
            <option value="">Select</option>
            {stateArray.map(abbreviation => (
              <option key={abbreviation} value={abbreviation}>
                {abbreviation}
              </option>
            ))}
          </TextField>
          <TextField
            id={`${FIELD_ID}-zip`}
            label="Zip code"
            value={draft.zip}
            error={zipInvalid}
            helperText={zipInvalid ? '5 digits' : undefined}
            onChange={e => update('zip', e.target.value)}
            autoComplete="off"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 5 }}
            fullWidth
          />
        </Box>
      </Section>

      <Section title="Bike">
        <TextField
          id={`${FIELD_ID}-year`}
          label="Year"
          value={draft.year || ''}
          error={yearInvalid}
          helperText={yearInvalid ? `${MIN_YEAR} to ${new Date().getFullYear() + 2}` : undefined}
          onChange={e => update('year', e.target.value)}
          autoComplete="off"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4 }}
          fullWidth
        />
        <Autocomplete
          freeSolo
          options={brands}
          inputValue={draft.brand}
          onInputChange={(_event, value) => update('brand', value)}
          renderInput={params => (
            <TextField
              {...params}
              id={`${FIELD_ID}-brand`}
              label="Brand"
              inputProps={{ ...params.inputProps, autoCapitalize: 'words' }}
            />
          )}
        />
        <TextField
          id={`${FIELD_ID}-model`}
          label="Model"
          value={draft.model}
          onChange={e => update('model', e.target.value)}
          autoComplete="off"
          fullWidth
        />
        <TextField
          id={`${FIELD_ID}-description`}
          label="Description (optional)"
          multiline
          minRows={3}
          value={draft.description}
          onChange={e => update('description', e.target.value)}
          fullWidth
        />
        <TextField
          id={`${FIELD_ID}-serialNumber`}
          label="Serial number (optional)"
          value={draft.serialNumber}
          onChange={e => update('serialNumber', e.target.value)}
          autoComplete="off"
          inputProps={{ autoCapitalize: 'characters', autoCorrect: 'off' }}
          fullWidth
        />
      </Section>

      <Section title="Photos">
        <PhotoCapture
          label="Drive side"
          hint="The whole bike from the chain side. Tap Add photo to take one or pick from your library."
          url={draft.imageDriveSideUrl}
          progress={progress.imageDriveSideUrl}
          error={photoErrors.imageDriveSideUrl}
          onSelect={file => handlePhoto('imageDriveSideUrl', file)}
          onClear={() => clearPhoto('imageDriveSideUrl')}
        />
        <PhotoCapture
          label="Non-drive side"
          hint="The whole bike from the other side."
          url={draft.imageNonDriveSideUrl}
          progress={progress.imageNonDriveSideUrl}
          error={photoErrors.imageNonDriveSideUrl}
          onSelect={file => handlePhoto('imageNonDriveSideUrl', file)}
          onClear={() => clearPhoto('imageNonDriveSideUrl')}
        />
      </Section>

      {error && <Alert severity="error">{error}</Alert>}

      <Dialog
        open={confirmingReset}
        onClose={() => setConfirmingReset(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Start over?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This clears everything entered so far, including the photos. It cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button size="large" onClick={() => setConfirmingReset(false)} sx={{ minHeight: 48 }}>
            Keep editing
          </Button>
          <Button
            size="large"
            color="error"
            variant="contained"
            onClick={resetForm}
            sx={{ minHeight: 48 }}
          >
            Clear everything
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          mx: -2,
          px: 2,
          pt: 1.5,
          pb: 'calc(12px + env(safe-area-inset-bottom))',
          bgcolor: '#F5F5F5',
          borderTop: 1,
          borderColor: 'grey.300',
        }}
      >
        {!canSubmit && missing.length > 0 && !submitting && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
            Still needed: {missing.slice(0, 3).join(', ')}
            {missing.length > 3 ? ` and ${missing.length - 3} more` : ''}
          </Typography>
        )}
        <Button
          fullWidth
          size="large"
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          sx={{ minHeight: 52 }}
        >
          {submitting ? <CircularProgress size={24} color="inherit" /> : 'Create submission'}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateSubmissionOnBehalf;
